import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../services/api';
import ScreenshotAnnotator from './ScreenshotAnnotator';
import { useBreadcrumb } from '../services/breadcrumb';
import './BugReports.css';

const CHAT_API_BASE = '/api/chat';

const authFetch = async (url, options = {}) => {
  const token = await api.ensureAuthenticated();
  return fetch(url, { ...options, headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` } });
};

const reporterName = (user) => {
  if (!user) return '';
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return user.fullName || full || user.username || '';
};

// Modal form to file a bug report or feature request. Reused by the global
// floating button and by the Bug Tracker page's "New report" action.
const BugReportModal = ({ user, currentPage = 'Dashboard', onClose, onSubmitted }) => {
  const [type, setType] = useState('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('Medium');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Live in-app location (e.g. "Projects → Smith Residence → Rooms → Kitchen →
  // Layout"), falling back to the top-level page label.
  const crumbs = useBreadcrumb();
  const pageLabel = (crumbs && crumbs.length) ? crumbs.join(' → ') : currentPage;

  // Screenshot capture + annotation.
  const [capturing, setCapturing] = useState(false);
  const [rawCapture, setRawCapture] = useState(null);   // captured image awaiting annotation
  const [screenshot, setScreenshot] = useState(null);   // final annotated data URL to submit

  // Files to attach (uploaded to bug-attachments.php after the report is created).
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (arr.length) setFiles(prev => [...prev, ...arr]);
  };

  // Admin-only: the destination email a reported request can be forwarded to.
  // Non-admins never load or see this.
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifySaving, setNotifySaving] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  useEffect(() => {
    if (!user?.isAdmin) return;
    (async () => {
      try {
        const res = await authFetch(`${CHAT_API_BASE}/support-settings.php?user_id=${encodeURIComponent(user.id)}`);
        const data = await res.json().catch(() => ({}));
        if (data.success) setNotifyEmail(data.email || '');
      } catch (e) { /* ignore */ }
    })();
  }, [user?.isAdmin, user?.id]);
  const saveNotifyEmail = async () => {
    setNotifySaving(true); setNotifyMsg('');
    try {
      const res = await authFetch(`${CHAT_API_BASE}/support-settings.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, email: notifyEmail.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) { setNotifyEmail(data.email); setNotifyMsg('Saved'); setTimeout(() => setNotifyMsg(''), 1500); }
      else setNotifyMsg(data.message || 'Save failed');
    } catch (e) { setNotifyMsg('Save failed'); }
    setNotifySaving(false);
  };

  // Draggable position ({x,y} once dragged; null = centered by the overlay).
  const [pos, setPos] = useState(null);
  const dragRef = useRef(null);

  const startDrag = (e) => {
    if (e.button !== 0 || e.target.closest('.bug-modal-close')) return;
    const modal = e.currentTarget.closest('.bug-modal');
    if (!modal) return;
    const rect = modal.getBoundingClientRect();
    dragRef.current = { dx: e.clientX - rect.left, dy: e.clientY - rect.top, w: rect.width, h: rect.height };
    setPos({ x: rect.left, y: rect.top });
    window.addEventListener('mousemove', onDrag);
    window.addEventListener('mouseup', endDrag);
    e.preventDefault();
  };
  const onDrag = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const x = Math.min(Math.max(0, e.clientX - d.dx), window.innerWidth - Math.min(d.w, 200));
    const y = Math.min(Math.max(0, e.clientY - d.dy), window.innerHeight - 40);
    setPos({ x, y });
  }, []);
  const endDrag = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onDrag);
    window.removeEventListener('mouseup', endDrag);
  }, [onDrag]);

  const captureScreen = async () => {
    setError('');
    setCapturing(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      // Skip our own floating UI so the shot shows only the app page.
      const skip = ['bug-modal-overlay', 'bug-fab-wrap', 'shot-annot-overlay'];
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        backgroundColor: '#1a1a1a',
        scale: 1,
        ignoreElements: (el) => !!(el.classList && skip.some(c => el.classList.contains(c))),
      });
      setRawCapture(canvas.toDataURL('image/png'));
    } catch (e) {
      setError('Could not capture the page: ' + (e.message || 'unknown error'));
    }
    setCapturing(false);
  };

  const modalStyle = pos ? { position: 'fixed', left: pos.x, top: pos.y, margin: 0 } : undefined;

  const submit = async () => {
    if (!title.trim()) { setError('Please enter a short title.'); return; }
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`${CHAT_API_BASE}/bug-reports.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          priority,
          page_url: `${pageLabel} (${window.location.href})`.substring(0, 500),
          reporter_user_id: user?.id,
          reporter_name: reporterName(user),
          screenshot: screenshot || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        // Upload any attached files against the new report (non-fatal on failure).
        const bugId = data.data?.id;
        if (bugId && files.length) {
          for (const f of files) {
            const fd = new FormData();
            fd.append('file', f);
            fd.append('bug_report_id', bugId);
            try { await authFetch(`${CHAT_API_BASE}/bug-attachments.php`, { method: 'POST', body: fd }); }
            catch (e) { /* report already filed; ignore attachment error */ }
          }
        }
        setDone(true);
        if (onSubmitted) onSubmitted(data.data);
      } else {
        setError(data.message || 'Failed to submit. Please try again.');
      }
    } catch (e) {
      setError('Failed to submit: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <>
      <div className="bug-modal-overlay" onClick={onClose}>
        <div className="bug-modal" style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div className="bug-modal-header bug-modal-drag" onMouseDown={startDrag}>
            <h2>{done ? 'Thanks!' : 'Report a bug or request a feature'}</h2>
            <button className="bug-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>

          {done ? (
            <div className="bug-modal-body">
              <p className="bug-done-msg">
                Your {type === 'feature' ? 'feature request' : 'bug report'} has been submitted. You can track its
                status any time from the <strong>Bug Tracker</strong>.
              </p>
              <div className="bug-modal-actions">
                <button className="bug-btn-primary" onClick={onClose}>Done</button>
              </div>
            </div>
          ) : (
            <div className="bug-modal-body">
              <div className="bug-type-toggle">
                <button
                  type="button"
                  className={`bug-type-option ${type === 'bug' ? 'active' : ''}`}
                  onClick={() => setType('bug')}
                >🐞 Bug</button>
                <button
                  type="button"
                  className={`bug-type-option ${type === 'feature' ? 'active' : ''}`}
                  onClick={() => setType('feature')}
                >💡 Feature request</button>
              </div>

              <label className="bug-field-label">Title <span className="bug-req">*</span></label>
              <input
                className="bug-input"
                type="text"
                placeholder={type === 'feature' ? 'What would you like to see?' : 'What went wrong?'}
                value={title}
                maxLength={255}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
              />

              <label className="bug-field-label">Details</label>
              <textarea
                className="bug-textarea"
                rows={5}
                placeholder={type === 'feature'
                  ? 'Describe the feature and why it would help…'
                  : 'Steps to reproduce, what you expected, what happened…'}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />

              <label className="bug-field-label">Screenshot</label>
              {screenshot ? (
                <div className="bug-shot-preview">
                  <img src={screenshot} alt="attached screenshot" />
                  <div className="bug-shot-preview-actions">
                    <button type="button" className="bug-btn-secondary" onClick={() => setRawCapture(screenshot)}>Edit</button>
                    <button type="button" className="bug-btn-secondary" onClick={() => captureScreen()} disabled={capturing}>Recapture</button>
                    <button type="button" className="bug-btn-danger" onClick={() => setScreenshot(null)}>Remove</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="bug-capture-btn" onClick={captureScreen} disabled={capturing}>
                  {capturing ? 'Capturing…' : '📷 Capture this page'}
                </button>
              )}

              <label className="bug-field-label">Attachments</label>
              <div className="bug-attach">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                />
                <button type="button" className="bug-btn-secondary" onClick={() => fileInputRef.current?.click()}>
                  📎 Attach files
                </button>
                {files.length > 0 && (
                  <ul className="bug-attach-list">
                    {files.map((f, i) => (
                      <li key={i}>
                        <span className="bug-attach-name" title={f.name}>{f.name}</span>
                        <button type="button" className="bug-attach-remove"
                          onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))} aria-label="Remove">×</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="bug-field-row">
                <div>
                  <label className="bug-field-label">Priority</label>
                  <select className="bug-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
                    {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div className="bug-page-hint">
                  <label className="bug-field-label">Page</label>
                  <div className="bug-page-value" title={`${pageLabel}\n${window.location.href}`}>{pageLabel}</div>
                </div>
              </div>

              {user?.isAdmin && (
                <div className="bug-admin-setting">
                  <label className="bug-field-label">
                    Support notification email(s) <span className="bug-admin-tag">admin only</span>
                  </label>
                  <div className="bug-notify-row">
                    <input
                      className="bug-input"
                      type="text"
                      value={notifyEmail}
                      placeholder="e.g. a@example.com, b@example.com"
                      onChange={(e) => setNotifyEmail(e.target.value)}
                    />
                    <button type="button" className="bug-btn-secondary" onClick={saveNotifyEmail} disabled={notifySaving}>
                      {notifySaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <div className="bug-notify-hint">
                    New requests and any responses added are automatically emailed to these addresses. Separate multiple with commas. Only admins see this.{notifyMsg && <strong> {notifyMsg}</strong>}
                  </div>
                </div>
              )}

              {error && <div className="bug-error">{error}</div>}

              <div className="bug-modal-actions">
                <button className="bug-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="bug-btn-primary" onClick={submit} disabled={saving || !title.trim()}>
                  {saving ? 'Submitting…' : 'Submit'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {rawCapture && (
        <ScreenshotAnnotator
          imageDataUrl={rawCapture}
          onCancel={() => setRawCapture(null)}
          onSave={(dataUrl) => { setScreenshot(dataUrl); setRawCapture(null); }}
        />
      )}
    </>
  );
};

export default BugReportModal;
